import { Modal, ModalBody, ModalHeader, Table, Button } from "reactstrap"
import React, { useEffect, useState } from "react"
import { MdDownload, MdStart } from "react-icons/md"
import * as XLSX from "xlsx"
import { saveAs } from "file-saver"
import { DatePicker, message } from "antd"
import moment from "moment"
import Loader from "components/Loader/loader"
import { FetchPostData } from "utils/Common_api_functions"
import { FaRegPlayCircle } from "react-icons/fa"
import Swal from "sweetalert2"

const { RangePicker } = DatePicker

const ExportModal = ({ exportModal, setExportModal, defaultPayrollMethod }) => {
  const [payrollMethod, setPayrollMethod] = useState("")
  const [selectedRange, setSelectedRange] = useState([])

  const [allData, setAllData] = useState([])

  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (exportModal) {
      setPayrollMethod(defaultPayrollMethod || "")
      setSelectedRange([])
      setAllData([])
    }
  }, [exportModal, defaultPayrollMethod])

  const handleRangeChange = dates => {
    if (!dates || dates.length !== 2) {
      return setSelectedRange([])
    }

    if (!payrollMethod) {
      message.error("Please select a payroll frequency first")
      return
    }

    const [start, end] = dates
    const diff = end.diff(start, "days") + 1

    if (payrollMethod === "weekly" && diff > 7) {
      message.error("Dates cannot exceed 7 days")
      return
    }

    if (payrollMethod === "fortnightly" && diff > 15) {
      message.error("Dates cannot exceed 15 days")
      return
    }

    if (payrollMethod === "monthly") {
      if (!start.isSame(end, "month")) {
        message.error("Monthly range must be within the same month")
        return
      }
    }

    setSelectedRange(dates)
  }

  const handleGenerate = async () => {
    if (!payrollMethod) {
      message.error("Please select a payroll frequency")
      return
    }

    if (!selectedRange || selectedRange.length !== 2) {
      message.error("Please select a date range")
      return
    }

    let startDate = null
    let endDate = null

    startDate = moment(selectedRange[0].toDate()).startOf("day")
    endDate = moment(selectedRange[1].toDate()).endOf("day")

    if (!startDate || !endDate) {
      setAllData([])
      return
    }

    try {
      setLoading(true)

      const res = await FetchPostData(setLoading, "payroll/generate/sheet", {
        frequency: payrollMethod,
        fromDate: startDate.format("YYYY-MM-DD"),
        toDate: endDate.format("YYYY-MM-DD"),
      })

      const payroll = res?.data?.payrollSheet?.employees

      setAllData(payroll || [])
    } catch (err) {
      console.error(err)
      setAllData([])
    } finally {
      setLoading(false)
    }
  }

  const handleDownload = async () => {
    if (!allData?.length) return

    const confirmResult = await Swal.fire({
      icon: "question",
      title: "Do you want to download the payroll report?",
      text: "After this you won't be able to change in this payout of the employees",
      showCancelButton: true,
      confirmButtonText: "Yes, download",
      cancelButtonText: "No",
    })

    if (!confirmResult.isConfirmed) return

    try {
      setLoading(true)

      let startDate = null
      let endDate = null

      startDate = moment(selectedRange[0].toDate()).startOf("day")
      endDate = moment(selectedRange[1].toDate()).endOf("day")

      const res = await FetchPostData(setLoading, "payroll/approve/sheet", {
        data: allData,
        frequency: payrollMethod,
        fromDate: startDate.format("YYYY-MM-DD"),
        toDate: endDate.format("YYYY-MM-DD"),
      })

      const payroll = res?.data?.payrollSheet?.employees

      setAllData(payroll || [])
    } catch (err) {
      console.error(err)
      setAllData([])
    } finally {
      setLoading(false)
    }

    // Proceed with Excel download
    const worksheet = XLSX.utils.json_to_sheet(
      allData.map(item => ({
        Name: item?.employeeName?.split(" ")[0],
        Surname: item?.employeeName?.split(" ").slice(1).join(" "),
        "Hours Worked": Math.round(Number(item?.totalHours || 0)),
        Email: item.email,
        Frequency: item.paymentFrequency,
      }))
    )

    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, worksheet, "Employees")

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" })
    saveAs(
      new Blob([excelBuffer]),
      `Employees_${moment().format("YYYY-MM-DD")}.xlsx`
    )

    Swal.fire({
      icon: "success",
      title: "Downloaded!",
      text: "Payroll report has been downloaded successfully.",
      timer: 2000,
      showConfirmButton: false,
    })

    setExportModal(false)
  }

  return (
    <Modal isOpen={exportModal} fullscreen>
      {loading && <Loader />}
      <ModalHeader toggle={() => setExportModal(false)}>
        <h5 className="mb-0">Export Payout Data</h5>
      </ModalHeader>

      <ModalBody>
        <div className="px-5">
          <div className="d-flex justify-content-end">
            <div className="d-flex gap-3 me-3 align-items-center">
              <select
                className="form-select w-auto pe-5"
                value={payrollMethod}
                onChange={e => {
                  setPayrollMethod(e.target.value)
                  setSelectedRange([])
                }}
              >
                <option value="">Select Payroll Frequency</option>
                <option value="weekly">Weekly</option>
                <option value="fortnightly">Fortnightly</option>
                <option value="monthly">Monthly</option>
              </select>
              <RangePicker
                value={selectedRange}
                onChange={handleRangeChange}
                format="DD/MM/YYYY"
                placeholder={["Start Date", "End Date"]}
                style={{ height: "37px" }}
              />
            </div>
            <Button color="primary" className="me-3" onClick={handleGenerate}>
              <FaRegPlayCircle className="me-1" size={14} /> Generate
            </Button>
            {allData?.length > 0 && (
              <Button color="primary" onClick={handleDownload}>
                <MdDownload className="me-1" size={14} /> Download
              </Button>
            )}
          </div>
          <Table className="bg-white mt-3" responsive bordered>
            <thead>
              <tr>
                <th>Sr No.</th>
                <th>Name</th>
                <th>Surname</th>
                <th>Time Sheet - Hours worked </th>
                <th>Email Address</th>
                <th>Payment Frequency</th>
              </tr>
            </thead>
            <tbody>
              {allData?.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center">
                    No Data Found{" "}
                  </td>
                </tr>
              )}

              {allData?.map((item, index) => {
                return (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{item?.employeeName?.split(" ")[0]}</td>
                    <td>{item?.employeeName?.split(" ").slice(1).join(" ")}</td>
                    <td>{Math.round(Number(item?.totalHours || 0))}</td>
                    <td>{item?.email}</td>
                    <td>{item?.paymentFrequency}</td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        </div>
      </ModalBody>
    </Modal>
  )
}

export default ExportModal
